import { useEffect, useRef } from 'react';
import { useState } from 'react';

const useTextToSpeech = (text: string) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Handle cleanup on page unload/reload
  useEffect(() => {
    const handleUnload = () => {
      window.speechSynthesis.cancel();
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    utteranceRef.current = null;
  };

  const handleSpeak = (message: string) => {
    // If this instance is currently speaking, stop it
    if (isSpeaking) {
      stopSpeaking();
      return;
    }

    // Cancel any other ongoing speech
    stopSpeaking();

    try {
      const utterance = new SpeechSynthesisUtterance(message);
      utteranceRef.current = utterance;

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => {
        stopSpeaking();
      };
      utterance.onerror = () => {
        stopSpeaking();
        console.error('Speech synthesis error occurred');
      };

      // Force stop any ongoing speech before starting new one
      window.speechSynthesis.cancel();

      // Small delay to ensure previous speech is fully cancelled
      setTimeout(() => {
        window.speechSynthesis.speak(utterance);
      }, 50);
    } catch (error) {
      console.error('Error in speech synthesis:', error);
      stopSpeaking();
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      stopSpeaking();
    };
  }, []);

  return {
    isSpeaking,
    handleSpeak,
  };
};

export default useTextToSpeech;
